import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getStudents,
  updateStudentProfileFromAdminSide,
  activateUserAccount,
  deactivateUserAccount,
  getStudent,
  retrieveStudentDetails,
} from "../../../services/admin.services";
import { getFinalizedApplication } from "../../../services/application.services";
import { getApplication } from "../../../services/user.services";
import {
  setAllStudents,
  updateStudentArchive,
  updateStudentField,
} from "../../../state/reducers/appSlice";
import { getCustomers } from "../../../services/customer.services";
import { setAllCustomers } from "../../../state/reducers/customerSlice";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { Download, Filter, X, ChevronDown, ChevronUp, Check, Search, Flag, GraduationCap, Calendar } from "lucide-react";

// Multi-Select Dropdown Component
const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  icon: Icon,
  disabled = false,
  showSearch = true,
  searchPlaceholder = "Search options...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  const filteredOptions = showSearch
    ? options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (showSearch) {
          setSearchTerm("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearch]);

  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const handleOptionToggle = (optionValue) => {
    const newSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((value) => value !== optionValue)
      : [...selectedValues, optionValue];

    onChange(newSelectedValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      const filteredValues = filteredOptions.map((option) => option.value);
      const newSelectedValues = selectedValues.filter(
        (value) => !filteredValues.includes(value)
      );
      onChange(newSelectedValues);
    } else {
      const filteredValues = filteredOptions.map((option) => option.value);
      const newSelectedValues = [
        ...new Set([...selectedValues, ...filteredValues]),
      ];
      onChange(newSelectedValues);
    }
  };

  const handleClearAll = () => {
    if (showSearch && searchTerm) {
      const filteredValues = filteredOptions.map((option) => option.value);
      const newSelectedValues = selectedValues.filter(
        (value) => !filteredValues.includes(value)
      );
      onChange(newSelectedValues);
    } else {
      onChange([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const option = options.find((opt) => opt.value === selectedValues[0]);
      return option ? option.label : selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full border border-gray-300 rounded-md p-2 text-sm text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between ${
          disabled
            ? "bg-gray-100 cursor-not-allowed"
            : "bg-white hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center flex-1 min-w-0">
          {Icon && (
            <Icon size={14} className="mr-2 text-blue-500 flex-shrink-0" />
          )}
          <span
            className={`truncate ${
              selectedValues.length === 0 ? "text-gray-500" : "text-gray-700"
            }`}
          >
            {getDisplayText()}
          </span>
          {selectedValues.length > 0 && (
            <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0">
              {selectedValues.length}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp size={16} className="text-gray-400" />
        ) : (
          <ChevronDown size={16} className="text-gray-400" />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg flex flex-col max-h-72">
          {showSearch && (
            <div className="bg-white px-3 py-2 border-b border-gray-200 flex-shrink-0">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Search
                  size={16}
                  className="absolute left-2.5 top-2.5 text-gray-400"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {selectedValues.length === options.length
                ? "Deselect All"
                : "Select All"}
            </button>
            {selectedValues.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-red-600 hover:text-red-800 font-medium"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="py-1 overflow-y-auto flex-1 min-h-0">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => handleOptionToggle(option.value)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-3"
                  />
                  <span className="text-sm text-gray-700 flex-1">
                    {option.label}
                  </span>
                  {selectedValues.includes(option.value) && (
                    <Check size={14} className="text-blue-600 ml-2" />
                  )}
                </label>
              ))
            ) : (
              <div className="px-3 py-4 text-center">
                <Search className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">
                  {showSearch && searchTerm
                    ? `No options found for "${searchTerm}"`
                    : "No options available"}
                </p>
              </div>
            )}
          </div>

          {options.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Selected Values Display Component
const SelectedValuesDisplay = ({
  selectedValues,
  options,
  onRemove,
  label,
}) => {
  if (selectedValues.length === 0) return null;

  return (
    <div className="mt-2">
      <div className="text-xs text-gray-600 mb-1">{label}:</div>
      <div className="flex flex-wrap gap-1">
        {selectedValues.map((value) => {
          const option = options.find((opt) => opt.value === value);
          const displayName = option ? option.label : value;

          return (
            <span
              key={value}
              className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
            >
              {displayName}
              <button
                type="button"
                onClick={() => onRemove(value)}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
};

const isStudentArchived = (student) =>
  student?.loan_types && student.loan_types.__archived__ === true;

const normalizeKey = (val) =>
  String(val || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const normalizeStatus = (val) => normalizeKey(val).replace(/_/g, "");

const isFinalizedStatus = (status) => {
  const s = normalizeStatus(status);
  return s.includes("finalized") || s.includes("finalised");
};

const getStatusValue = (entry) => {
  if (!entry || typeof entry !== "object") return "";
  const direct =
    entry.status ?? entry.Status ?? entry.current_status ?? entry.currentStatus;
  if (typeof direct === "string") return direct;

  const found = Object.entries(entry).find(([k, v]) => {
    const nk = normalizeKey(k);
    return nk.includes("status") && typeof v === "string";
  });
  return found ? found[1] : "";
};

const extractUniversityName = (entry) => {
  if (!entry || typeof entry !== "object") return "—";

  // Fast-path: common keys
  const direct =
    entry.university ??
    entry.university_name ??
    entry.universityName ??
    entry["university name"] ??
    entry.University ??
    entry["University Name"] ??
    entry["University"];
  if (typeof direct === "string" && direct.trim()) return direct;

  // Robust fallback: scan keys and pick first string that belongs to "university"
  const found = Object.entries(entry).find(([k, v]) => {
    const nk = normalizeKey(k);
    if (!nk) return false;
    if (!nk.includes("university")) return false;
    return typeof v === "string" && v.trim();
  });

  if (found) return found[1];

  return "—";
};

const formatMonthYearOnly = (value) => {
  if (value == null) return "—";
  const raw = String(value).trim();
  if (!raw) return "—";

  // Remove ordinal suffixes like "1st", "2nd", etc.
  const cleaned = raw.replace(/(\d+)(st|nd|rd|th)/, "$1");

  const d = new Date(cleaned);
  if (!isNaN(d)) {
    return d.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  }

  // If it's already something like "March 2026", keep it as-is
  return raw;
};

const formatStatusLabel = (status) => {
  if (!status) return "—";
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized.includes("complete")) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  }
  if (normalized.includes("document")) {
    return "bg-amber-50 text-amber-700 ring-amber-200";
  }
  if (normalized.includes("university") || normalized.includes("shortlisting")) {
    return "bg-sky-50 text-sky-700 ring-sky-200";
  }
  if (normalized.includes("pending")) {
    return "bg-amber-50 text-amber-700 ring-amber-200";
  }
  if (normalized.includes("reject") || normalized.includes("fail")) {
    return "bg-rose-50 text-rose-700 ring-rose-200";
  }
  return "bg-indigo-50 text-indigo-700 ring-indigo-200";
};

const hasUniversityValue = (entry) => {
  const uni = extractUniversityName(entry);
  if (!(typeof uni === "string")) return false;
  const clean = uni.trim();
  if (!clean || clean === "—") return false;
  const normalized = normalizeKey(clean);
  if (normalized === "university" || normalized === "universityname")
    return false;
  return true;
};

const pickAdmittedEntry = (entries) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;

  // First priority: a row explicitly marked finalized/finalised
  const finalizedRow = entries.find((e) => isFinalizedStatus(getStatusValue(e)));
  if (finalizedRow && hasUniversityValue(finalizedRow)) return finalizedRow;

  // Second priority: any row that has a usable university value
  const universityRow = entries.find((e) => hasUniversityValue(e));
  if (universityRow) return universityRow;

  return null;
};

const EMPTY_COMPLETED_DETAILS = {
  university: "—",
  country: "—",
  course: "—",
  intake: "—",
};

const extractCountryFromEntry = (entry) => {
  if (!entry || typeof entry !== "object") return "—";
  const direct =
    entry.country ??
    entry.Country ??
    entry.country_name ??
    entry.countryName ??
    entry["country name"] ??
    entry["Country"];
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const found = Object.entries(entry).find(([k, v]) => {
    const nk = normalizeKey(k);
    return nk.includes("country") && typeof v === "string" && v.trim();
  });
  return found ? String(found[1]).trim() : "—";
};

const extractCourseFromEntry = (entry) => {
  if (!entry || typeof entry !== "object") return "—";

  // 1) Prefer explicit course-related keys
  const directCourse =
    entry.course ??
    entry.course_name ??
    entry.courseName ??
    entry.Course ??
    entry["course name"] ??
    entry["Course Name"];
  if (typeof directCourse === "string" && directCourse.trim())
    return directCourse.trim();

  // 2) Fallback: some UIs store the program under `program name`
  const directProgram =
    entry.program_name ??
    entry.programName ??
    entry.Program ??
    entry["program name"] ??
    entry["Program Name"];
  if (typeof directProgram === "string" && directProgram.trim())
    return directProgram.trim();

  // 3) Fallback: some UIs store the course/program under `name`
  const directName =
    entry.name ?? entry.Name ?? entry["course"] ?? entry["Course"];
  if (typeof directName === "string" && directName.trim())
    return directName.trim();

  // 4) Last resort: scan keys
  const found = Object.entries(entry).find(([k, v]) => {
    const nk = normalizeKey(k);
    return (
      (nk.includes("course") || nk === "name") && typeof v === "string" && v.trim()
    );
  });
  return found ? String(found[1]).trim() : "—";
};

const extractIntakeFromEntry = (entry) => {
  if (!entry || typeof entry !== "object") return "—";

  // If intake month/year are stored separately
  const month = entry.intakeMonth ?? entry.intake_month ?? entry.IntakeMonth;
  const year = entry.intakeYear ?? entry.intake_year ?? entry.IntakeYear;

  if (month && year) {
    const monthStr = String(month).trim();
    const yearStr = String(year).trim();
    if (monthStr && yearStr) {
      const monthNum = Number(monthStr);
      if (!Number.isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
        const d = new Date(`${yearStr}-${String(monthNum).padStart(2, "0")}-01`);
        if (!isNaN(d)) {
          return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
        }
      }

      return formatMonthYearOnly(`${monthStr} ${yearStr}`);
    }
  }

  const direct =
    entry.intake ??
    entry.Intake ??
    entry.intake_date ??
    entry.intakeDate ??
    entry["intake date"] ??
    entry["Intake Date"] ??
    entry.semester ??
    entry.Semester ??
    entry.term ??
    entry.Term ??
    entry["intake month"] ??
    entry.intake_month ??
    entry.intake_year;

  if (typeof direct === "string" && direct.trim()) {
    return formatMonthYearOnly(direct.trim());
  }
  if (typeof direct === "number" && !Number.isNaN(direct)) {
    return String(direct);
  }

  const found = Object.entries(entry).find(([k, v]) => {
    const nk = normalizeKey(k);
    if (
      !nk.includes("intake") &&
      !nk.includes("semester") &&
      !nk.includes("term") &&
      !nk.includes("year")
    ) {
      return false;
    }
    if (typeof v === "string" && v.trim()) return true;
    if (typeof v === "number" && !Number.isNaN(v)) return true;
    return false;
  });
  if (!found) return "—";
  const v = found[1];
  if (typeof v === "string") return formatMonthYearOnly(v.trim());
  return typeof v === "number" ? String(v) : String(v).trim();
};

const buildCompletedDetailsFromEntry = (entry) => {
  if (!entry) return { ...EMPTY_COMPLETED_DETAILS };
  const uni = extractUniversityName(entry);
  return {
    university: uni && uni !== "—" ? uni : "—",
    country: extractCountryFromEntry(entry),
    course: extractCourseFromEntry(entry),
    intake: extractIntakeFromEntry(entry),
  };
};

function ApplicationListTable({ archived = false, showCompletedOnly = false }) {
  const dispatch = useDispatch();
  const allStudents = useSelector((state) => state.app.allStudents);
  const allStudentsLoaded = useSelector(
    (state) => state.app.allStudentsLoaded
  );
  const allCustomers = useSelector((state) => state.customer.allCustomers);
  const allCustomersLoaded = useSelector(
    (state) => state.customer.allCustomersLoaded
  );

  const [completedDetailsMap, setCompletedDetailsMap] = useState({});
  const fallbackFetchedRef = useRef(new Set());

  useEffect(() => {
    if (!allCustomersLoaded) {
      const fetchAllCustomers = async () => {
        try {
          const response = await getCustomers();
          const allData = Array.isArray(response?.data?.data) ? response.data.data : [];
          dispatch(setAllCustomers(allData));
        } catch (err) {
          console.error("Error fetching all customers in ApplicationListTable:", err);
        }
      };
      fetchAllCustomers();
    }
  }, [dispatch, allCustomersLoaded]);

  const students = useMemo(() => {
    return allStudents.filter((s) => {
      const isArch = isStudentArchived(s);
      const isComplete = (s?.status || "") === "complete";

      if (archived) return isArch;
      if (isArch) return false;
      if (showCompletedOnly) return isComplete;
      return !isComplete;
    });
  }, [allStudents, archived, showCompletedOnly]);

  const tableColSpan = showCompletedOnly ? 11 : 9;

  useEffect(() => {
    let cancelled = false;

    const loadMap = async () => {
      if (!showCompletedOnly) {
        setCompletedDetailsMap({});
        return;
      }

      try {
        const finalizedStudents = await getFinalizedApplication();
        if (cancelled) return;

        const map = {};

        (finalizedStudents || []).forEach((stu) => {
          const studentId = stu?.student_id;
          if (studentId == null) return;

          const finalizedUniversities = Array.isArray(
            stu?.finalized_universities
          )
            ? stu.finalized_universities
            : [];

          const admittedUni = pickAdmittedEntry(finalizedUniversities);

          map[String(studentId)] = buildCompletedDetailsFromEntry(admittedUni);
        });

        setCompletedDetailsMap(map);
      } catch (err) {
        console.error("Failed to load completed student details map:", err);
        setCompletedDetailsMap({});
      }
    };

    loadMap();
    return () => {
      cancelled = true;
    };
  }, [showCompletedOnly, allStudentsLoaded]);

  // Fallback: if the finalized-university map didn't include a student (or still shows "—"),
  // fetch that student's full profile and derive admitted university from `is_finalized_excel`.
  useEffect(() => {
    if (!showCompletedOnly) return;
    if (!students || students.length === 0) return;

    const missingIds = students
      .map((s) => s.student_id)
      .filter((id) => id != null)
      .map((id) => String(id))
      .filter((id) => {
        const d = completedDetailsMap[id];
        return !d || !d.university || d.university === "—" || d.intake === "—";
      })
      .filter((id) => !fallbackFetchedRef.current.has(id));

    if (missingIds.length === 0) return;

    missingIds.forEach((studentId) => {
      fallbackFetchedRef.current.add(studentId);
      (async () => {
        try {
          const res = await retrieveStudentDetails(studentId);
          const studentObj = res?.data?.data ?? res?.data ?? {};
          const entries = Array.isArray(studentObj?.is_finalized_excel)
            ? studentObj.is_finalized_excel
            : [];

          const admittedEntry = pickAdmittedEntry(entries);

              const details = buildCompletedDetailsFromEntry(admittedEntry);

              // Intake Date is shown in "Student Application Details" using `applicationData.intake`,
              // so if `intake` is missing from the finalized-university row, pull it from application.
              try {
                const appRes = await getApplication(studentId);
                const appData =
                  appRes?.data?.data ?? appRes?.data ?? appRes ?? {};
                const intakeValue =
                  appData.intake ??
                  appData.intake_date ??
                  appData.intakeDate ??
                  appData["intake date"];

                const intakeFormatted = formatMonthYearOnly(intakeValue);
                if (intakeFormatted && intakeFormatted !== "—") {
                  details.intake = intakeFormatted;
                }
              } catch (err) {
                // If application fetch fails, we still keep the other finalized-university derived fields.
                console.error(
                  "Failed to fetch application intake for student:",
                  studentId,
                  err
                );
              }

              setCompletedDetailsMap((prev) => ({
                ...prev,
                [studentId]: details,
              }));
        } catch (err) {
          console.error(
            "Failed to load completed details fallback for student:",
            studentId,
            err
          );
        }
      })();
    });
  }, [showCompletedOnly, students, completedDetailsMap]);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingIds, setLoadingIds] = useState([]);
  const navigate = useNavigate();
  const [studentsPerPage, setStudentsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    country: [],
    intake: [],
    intendedDegree: [],
  });

  const uniqueCountry = useMemo(() => {
    if (showCompletedOnly) {
      const countries = new Set();
      (students || []).forEach((student) => {
        const d = completedDetailsMap[String(student.student_id)];
        if (d && d.country && d.country !== "—") {
          const trimmed = String(d.country).trim();
          if (trimmed) countries.add(trimmed);
        }
      });
      return Array.from(countries).sort((a, b) => a.localeCompare(b));
    }

    if (!allCustomers) return [];
    return Array.from(
      new Set(
        allCustomers
          .flatMap((c) => c.preferred_countries || [])
          .map((c) => (typeof c === "string" ? c.trim() : ""))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [showCompletedOnly, students, completedDetailsMap, allCustomers]);

  const countryOptions = useMemo(() => {
    return uniqueCountry.map((c) => ({ value: c, label: c }));
  }, [uniqueCountry]);

  const intendedDegreeOptions = [
    { value: "Undergraduate/Bachelors", label: "Undergraduate/Bachelors" },
    { value: "PG Diploma", label: "PG Diploma" },
    { value: "Masters", label: "Masters" },
    { value: "MBA", label: "MBA" },
    { value: "Phd", label: "PhD" },
  ];

  const intakeOptions = useMemo(() => {
    const options = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    for (let year = currentYear - 5; year <= currentYear + 5; year++) {
      for (let month = 0; month < 12; month++) {
        const date = new Date(year, month);
        const formattedDate =
          date.toLocaleString("default", { month: "short" }) + "-" + year;
        options.push({
          value: `${year}-${month + 1 < 10 ? "0" : ""}${month + 1}`,
          label: formattedDate,
        });
      }
    }
    return options;
  }, []);



  useEffect(() => {
    if (allStudentsLoaded) {
      setLoading(false);
      return;
    }
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const [studentsRes, usersRes] = await Promise.all([
          getStudents(),
          getStudent(),
        ]);

        const userMap = {};
        usersRes.data.data.forEach((user) => {
          userMap[user.id] = user;
        });

        const allMerged = studentsRes.data.data.map((student) => ({
          ...student,
          user: userMap[student.student_id] || {},
        }));

        dispatch(setAllStudents(allMerged));
      } catch (error) {
        toast.error("Failed to fetch students");
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [dispatch, allStudentsLoaded]);



  // Handle toggle visa process status
  const handleToggleStatus = async (studentId, currentStatus, event) => {
    event.stopPropagation();
    if (loadingIds.includes(studentId)) return;
    setLoadingIds((prev) => [...prev, studentId]);

    try {
      const newStatus = !currentStatus;
      await updateStudentProfileFromAdminSide(studentId, {
        open_visa_page: newStatus,
      });

      dispatch(
        updateStudentField({
          studentId,
          fields: { open_visa_page: newStatus },
        })
      );

      toast.success(
        newStatus ? "Visa Process Enabled" : "Visa Process Disabled"
      );
    } catch (error) {
      toast.error("Failed to update visa process status");
    } finally {
      setLoadingIds((prev) => prev.filter((id) => id !== studentId));
    }
  };

  // Handle CRM access toggle
  const handleCrmAccessToggle = async (student, event) => {
    event.stopPropagation();
    const studentId = student.student_id;
    if (loadingIds.includes(studentId)) return;
    setLoadingIds((prev) => [...prev, studentId]);

    try {
      const isActive = student.user?.is_active === true;

      if (isActive) {
        await deactivateUserAccount(studentId);
        dispatch(
          updateStudentField({
            studentId,
            fields: { user: { ...student.user, is_active: false } },
          })
        );
        toast.success("CRM Access Disabled");
      } else {
        await activateUserAccount(studentId);
        dispatch(
          updateStudentField({
            studentId,
            fields: { user: { ...student.user, is_active: true } },
          })
        );
        toast.success("CRM Access Enabled");
      }
    } catch (error) {
      toast.error(`Failed to update CRM access: ${error.message}`);
    } finally {
      setLoadingIds((prev) => prev.filter((id) => id !== studentId));
    }
  };

  // Navigate to detail page
  const handleRowClick = (studentId) => {
    navigate(`/admin/application/student/${studentId}`);
  };

  // Sort function
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (filterName, selectedValues) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: selectedValues,
    }));
    setCurrentPage(1);
  };

  const handleRemoveFilterValue = (filterName, valueToRemove) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: prev[filterName].filter((val) => val !== valueToRemove),
    }));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      country: [],
      intake: [],
      intendedDegree: [],
    });
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return Object.values(filters).some((filterGroup) => filterGroup.length > 0);
  };

  const handleArchiveToggle = async (studentId, shouldArchive, e) => {
    if (e) e.stopPropagation();
    const target = allStudents.find((s) => s.student_id === studentId);
    if (!target) return;

    const currentLoanTypes = target.loan_types || {};
    const newLoanTypes = shouldArchive
      ? { ...currentLoanTypes, __archived__: true }
      : { ...currentLoanTypes, __archived__: false };

    dispatch(updateStudentArchive({ studentId, loanTypes: newLoanTypes }));

    try {
      await updateStudentProfileFromAdminSide(studentId, {
        loan_types: newLoanTypes,
      });
      toast.success(
        shouldArchive
          ? "Student archived successfully"
          : "Student unarchived successfully"
      );
    } catch (error) {
      console.error("Archive failed:", error?.response?.data || error);
      toast.error("Failed to update archive status");
      dispatch(
        updateStudentArchive({ studentId, loanTypes: currentLoanTypes })
      );
    }
  };

  const handleStatusCompleteToggle = async (studentId, isComplete, e) => {
    if (e) e.stopPropagation();
    const target = allStudents.find((s) => s.student_id === studentId);
    if (!target) return;

    const newStatus = isComplete ? "complete" : "document_pending";
    const oldStatus = target.status;

    dispatch(updateStudentField({ studentId, fields: { status: newStatus } }));

    try {
      await updateStudentProfileFromAdminSide(studentId, {
        status: newStatus,
      });
      toast.success(
        isComplete
          ? "Student marked as complete"
          : "Student marked as incomplete"
      );
    } catch (error) {
      console.error("Status update failed:", error?.response?.data || error);
      toast.error("Failed to update status");
      dispatch(updateStudentField({ studentId, fields: { status: oldStatus } }));
    }
  };

  // Export to Excel
  const handleDownloadExcel = () => {
    const formattedData = filteredStudents.map((student, index) => {
      const sid = String(student.student_id);
      const details =
        completedDetailsMap[sid] || { ...EMPTY_COMPLETED_DETAILS };

      if (showCompletedOnly) {
        return {
          SrNo: index + 1,
          StudentName: student.username,
          UniversityFinalized: details.university,
          Country: details.country,
          Intake: details.intake,
          Course: details.course,
          ContactNumber: student.phone_number || "-",
          Email: student.email,
          VisaProcess: student.open_visa_page ? "Enabled" : "Disabled",
          CRMAccess: student.user?.is_active ? "Active" : "Inactive",
        };
      }

      return {
        SrNo: index + 1,
        StudentName: student.username,
        Email: student.email,
        MobileNumber: student.phone_number || "-",
        Location: student.city || "-",
        Status: student.status || "-",
        VisaProcess: student.open_visa_page ? "Enabled" : "Disabled",
        CRMAccess: student.user?.is_active ? "Active" : "Inactive",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Set column widths (order must match formattedData keys)
    const widths = showCompletedOnly
      ? [
          { wch: 5 },
          { wch: 22 },
          { wch: 28 },
          { wch: 14 },
          { wch: 12 },
          { wch: 22 },
          { wch: 15 },
          { wch: 30 },
          { wch: 15 },
          { wch: 15 },
        ]
      : [
          { wch: 5 }, // Sr No
          { wch: 20 }, // Student Name
          { wch: 30 }, // Email
          { wch: 15 }, // Mobile Number
          { wch: 15 }, // Location
          { wch: 15 }, // Status
          { wch: 15 }, // Visa Process
          { wch: 15 }, // CRM Access
        ];

    worksheet["!cols"] = widths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

    XLSX.writeFile(workbook, "students_list.xlsx");
  };

  // Filter and sort students
  const filteredStudents = React.useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();

    const matchesSearch = (student) => {
      if (!q) return true;
      const hay = [
        student.username,
        student.email,
        student.phone_number,
        student.city,
        student.status,
      ]
        .filter((v) => v != null && String(v).trim() !== "")
        .map((v) => String(v).toLowerCase());
      if (hay.some((s) => s.includes(q))) return true;

      if (showCompletedOnly) {
        const d =
          completedDetailsMap[String(student.student_id)] ||
          EMPTY_COMPLETED_DETAILS;
        const detailHay = [d.university, d.country, d.course, d.intake]
          .filter((v) => v != null && String(v).trim() !== "" && v !== "—")
          .map((v) => String(v).toLowerCase());
        if (detailHay.some((s) => s.includes(q))) return true;
      }

      if (!showCompletedOnly) {
        // Check customer profile for country, course, intended degree, etc.
        const customer = allCustomers.find(
          (c) => c.email === student.email || c.id === student.customer_id
        );
        if (customer) {
          const countries = Array.isArray(customer.preferred_countries)
            ? customer.preferred_countries
            : [];
          const course = customer.intended_course || "";
          const degree = customer.intended_degree || "";
          const branch = customer.branch || "";

          const customerHay = [...countries, course, degree, branch]
            .filter((v) => v != null && String(v).trim() !== "")
            .map((v) => String(v).toLowerCase());

          if (customerHay.some((s) => s.includes(q))) return true;
        }
      }

      return false;
    };

    let result = Array.isArray(students) ? students.filter(matchesSearch) : [];

    // Apply filters

    if (filters.country && filters.country.length > 0) {
      const selectedCountriesLower = filters.country.map((c) =>
        String(c).toLowerCase().trim()
      );
      result = result.filter((student) => {
        const d = completedDetailsMap[String(student.student_id)];
        if (d && d.country && d.country !== "—") {
          if (
            selectedCountriesLower.includes(String(d.country).trim().toLowerCase())
          ) {
            return true;
          }
        }
        if (showCompletedOnly) return false;

        const customer = allCustomers.find(
          (c) => c.email === student.email || c.id === student.customer_id
        );
        if (customer && Array.isArray(customer.preferred_countries)) {
          return customer.preferred_countries.some((c) =>
            selectedCountriesLower.includes(String(c).trim().toLowerCase())
          );
        }
        return false;
      });
    }

    if (filters.intake && filters.intake.length > 0) {
      result = result.filter((student) => {
        const d = completedDetailsMap[String(student.student_id)];
        if (d && d.intake && d.intake !== "—") {
          const dateObj = new Date(d.intake);
          if (!isNaN(dateObj)) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
            const yyyyMm = `${yyyy}-${mm}`;
            if (filters.intake.includes(yyyyMm)) return true;
          }
        }
        const customer = allCustomers.find(
          (c) => c.email === student.email || c.id === student.customer_id
        );
        if (customer && customer.intake) {
          const customerIntakeMonthYear = customer.intake.slice(0, 7);
          return filters.intake.includes(customerIntakeMonthYear);
        }
        return false;
      });
    }

    if (filters.intendedDegree && filters.intendedDegree.length > 0) {
      const selected = new Set(
        filters.intendedDegree.map((s) => s.trim().toLowerCase())
      );
      const ugSelected =
        selected.has("undergraduate") ||
        selected.has("undergraduate/bachelors");

      result = result.filter((student) => {
        const customer = allCustomers.find(
          (c) => c.email === student.email || c.id === student.customer_id
        );
        if (!customer) return false;

        const d = (customer.intended_degree || "").trim().toLowerCase();

        if (ugSelected) {
          if (
            d === "undergraduate" ||
            d === "Undergraduate" ||
            d === "undergraduate/bachelors" ||
            d === "bachelors"
          ) {
            return true;
          }
        }

        return selected.has(d);
      });
    }

    const completedSortField =
      sortConfig.key === "completed_university"
        ? "university"
        : sortConfig.key === "completed_country"
          ? "country"
          : sortConfig.key === "completed_intake"
            ? "intake"
            : sortConfig.key === "completed_course"
              ? "course"
              : null;

    if (sortConfig.key) {
      result.sort((a, b) => {
        // Handle nested properties for user.is_active
        if (sortConfig.key === "crm_access") {
          if ((a.user?.is_active === true) === (b.user?.is_active === true))
            return 0;
          if (sortConfig.direction === "ascending") {
            return a.user?.is_active === true ? 1 : -1;
          } else {
            return a.user?.is_active === true ? -1 : 1;
          }
        }

        if (completedSortField) {
          const da =
            completedDetailsMap[String(a.student_id)] || EMPTY_COMPLETED_DETAILS;
          const db =
            completedDetailsMap[String(b.student_id)] || EMPTY_COMPLETED_DETAILS;
          const va = String(da[completedSortField] || "").toLowerCase();
          const vb = String(db[completedSortField] || "").toLowerCase();
          if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
          if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
          return 0;
        }

        const va = a[sortConfig.key];
        const vb = b[sortConfig.key];
        if (va == null && vb == null) return 0;
        if (va == null) return sortConfig.direction === "ascending" ? -1 : 1;
        if (vb == null) return sortConfig.direction === "ascending" ? 1 : -1;

        // For normal properties
        if (va < vb) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (va > vb) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [
    students,
    searchTerm,
    sortConfig,
    filters,
    showCompletedOnly,
    completedDetailsMap,
    allCustomers,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      {/* Student's List Header with Count */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="text-xl font-semibold text-slate-800">
          {archived
            ? "Archived Students"
            : showCompletedOnly
              ? "Completed Students"
              : "Student's List"}
          {students.length > 0 && (
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({students.length} students)
            </span>
          )}
        </h2>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search box */}
          <div className="relative w-full sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder={
                showCompletedOnly
                  ? "Search name, email, phone, university, country, intake, course..."
                  : "Search name, email, phone, country, course, location, status..."
              }
              className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-4 text-slate-700 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
          </div>

          {/* Filter dropdown button */}
          <div>
            <button
              onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg transition-colors border ${
                hasActiveFilters()
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Filter size={18} />
              <span>Filters</span>
              {hasActiveFilters() && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-xs text-white">
                  {Object.values(filters).flat().length}
                </span>
              )}
              {filterMenuOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>

          {/* Export button */}
          <button
            onClick={handleDownloadExcel}
            className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 font-medium text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={filteredStudents.length === 0}
          >
            <Download size={18} />
            <span>Export Excel</span>
          </button>
        </div>
      </div>

      {/* Collapsible Filters Panel */}
      {filterMenuOpen && (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm relative" style={{ minHeight: "200px" }}>
          <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
            <div className="flex items-center gap-2">
              <Filter className="text-slate-500" size={18} />
              <h3 className="font-semibold text-slate-800 text-base">Filter Students</h3>
            </div>
            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 relative">

            {/* Country Filter */}
            <div className="space-y-2 relative" style={{ zIndex: 43 }}>
              <label className="flex items-center text-sm font-medium text-slate-700">
                <Flag size={14} className="mr-2 text-indigo-500" />
                Country
              </label>
              <MultiSelectDropdown
                options={countryOptions}
                selectedValues={filters.country}
                onChange={(values) => handleFilterChange("country", values)}
                placeholder="All Countries"
              />
              <SelectedValuesDisplay
                selectedValues={filters.country}
                options={countryOptions}
                onRemove={(value) => handleRemoveFilterValue("country", value)}
                label="Selected Countries"
              />
            </div>

            {/* Intake Filter */}
            <div className="space-y-2 relative" style={{ zIndex: 42 }}>
              <label className="flex items-center text-sm font-medium text-slate-700">
                <Calendar size={14} className="mr-2 text-indigo-500" />
                Intake
              </label>
              <MultiSelectDropdown
                options={intakeOptions}
                selectedValues={filters.intake}
                onChange={(values) => handleFilterChange("intake", values)}
                placeholder="All Intakes"
              />
              <SelectedValuesDisplay
                selectedValues={filters.intake}
                options={intakeOptions}
                onRemove={(value) => handleRemoveFilterValue("intake", value)}
                label="Selected Intakes"
              />
            </div>

            {/* Intended Degree Filter */}
            <div className="space-y-2 relative" style={{ zIndex: 41 }}>
              <label className="flex items-center text-sm font-medium text-slate-700">
                <GraduationCap size={14} className="mr-2 text-indigo-500" />
                Intended Degree
              </label>
              <MultiSelectDropdown
                options={intendedDegreeOptions}
                selectedValues={filters.intendedDegree}
                onChange={(values) => handleFilterChange("intendedDegree", values)}
                placeholder="All Degrees"
              />
              <SelectedValuesDisplay
                selectedValues={filters.intendedDegree}
                options={intendedDegreeOptions}
                onRemove={(value) => handleRemoveFilterValue("intendedDegree", value)}
                label="Selected Degrees"
              />
            </div>
          </div>
        </div>
      )}

      {/* Active filters display */}
      {hasActiveFilters() && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-gray-600 font-medium">
            Active filters:
          </span>



          {filters.country.map((filter) => (
            <div
              key={`country-tag-${filter}`}
              className="flex items-center rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-700"
            >
              <span>Country: {filter}</span>
              <button
                onClick={() => handleRemoveFilterValue("country", filter)}
                className="ml-1 rounded-full p-1 hover:bg-indigo-100"
              >
                <X size={14} />
              </button>
            </div>
          ))}

          {filters.intake.map((filter) => {
            const option = intakeOptions.find((o) => o.value === filter);
            const label = option ? option.label : filter;
            return (
              <div
                key={`intake-tag-${filter}`}
                className="flex items-center rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-700"
              >
                <span>Intake: {label}</span>
                <button
                  onClick={() => handleRemoveFilterValue("intake", filter)}
                  className="ml-1 rounded-full p-1 hover:bg-indigo-100"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}

          {filters.intendedDegree.map((filter) => (
            <div
              key={`intendedDegree-tag-${filter}`}
              className="flex items-center rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-700"
            >
              <span>Degree: {filter}</span>
              <button
                onClick={() => handleRemoveFilterValue("intendedDegree", filter)}
                className="ml-1 rounded-full p-1 hover:bg-indigo-100"
              >
                <X size={14} />
              </button>
            </div>
          ))}

          <button
            onClick={clearAllFilters}
            className="text-sm text-red-600 hover:text-red-800 underline"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Results count & Records per page selector */}
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div className="text-sm text-gray-600 mb-2 sm:mb-0">
          Showing {filteredStudents.length > 0 ? indexOfFirstStudent + 1 : 0} to{" "}
          {Math.min(indexOfLastStudent, filteredStudents.length)} of{" "}
          {filteredStudents.length} entries
          {searchTerm || hasActiveFilters() ? " (filtered)" : ""}
        </div>

        <div className="flex items-center">
          <label className="flex items-center text-sm text-gray-600">
            <span className="mr-2">Show</span>
            <select
              value={studentsPerPage}
              onChange={(e) => {
                setStudentsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing page size
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="ml-2">entries</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-slate-100/80 text-sm leading-normal text-slate-700">
              <th className="py-3 px-6 text-left">Sr No</th>
              {showCompletedOnly ? (
                <>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("username")}
                  >
                    <div className="flex items-center whitespace-nowrap">
                      Name of the student
                      {sortConfig.key === "username" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("completed_university")}
                  >
                    <div className="flex items-center whitespace-nowrap">
                      University finalized
                      {sortConfig.key === "completed_university" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("completed_country")}
                  >
                    <div className="flex items-center whitespace-nowrap">
                      Country
                      {sortConfig.key === "completed_country" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("completed_intake")}
                  >
                    <div className="flex items-center whitespace-nowrap">
                      Intake
                      {sortConfig.key === "completed_intake" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("completed_course")}
                  >
                    <div className="flex items-center whitespace-nowrap">
                      Course
                      {sortConfig.key === "completed_course" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("phone_number")}
                  >
                    <div className="flex items-center whitespace-nowrap">
                      Contact Number
                      {sortConfig.key === "phone_number" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("email")}
                  >
                    <div className="flex items-center whitespace-nowrap">
                      Email ID
                      {sortConfig.key === "email" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                </>
              ) : (
                <>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("username")}
                  >
                    <div className="flex items-center">
                      Student Name
                      {sortConfig.key === "username" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("email")}
                  >
                    <div className="flex items-center">
                      Email ID
                      {sortConfig.key === "email" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="py-3 px-6 text-left hidden sm:table-cell">
                    Mobile Number
                  </th>
                  <th
                    className="hidden cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70 md:table-cell"
                    onClick={() => requestSort("city")}
                  >
                    <div className="flex items-center">
                      Location
                      {sortConfig.key === "city" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                    onClick={() => requestSort("status")}
                  >
                    <div className="flex items-center">
                      Status
                      {sortConfig.key === "status" && (
                        <span className="ml-1">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </th>
                </>
              )}
              <th
                className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                onClick={() => requestSort("open_visa_page")}
              >
                <div className="flex items-center">
                  Visa Process
                  {sortConfig.key === "open_visa_page" && (
                    <span className="ml-1">
                      {sortConfig.direction === "ascending" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="cursor-pointer px-6 py-3 text-left hover:bg-slate-200/70"
                onClick={() => requestSort("crm_access")}
              >
                <div className="flex items-center">
                  CRM Access
                  {sortConfig.key === "crm_access" && (
                    <span className="ml-1">
                      {sortConfig.direction === "ascending" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
              <th className="py-3 px-6 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={tableColSpan} className="py-6 px-6 text-center">
                  <div className="flex justify-center items-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500"></div>
                    <span className="text-base text-gray-700">
                      Loading students...
                    </span>
                  </div>
                </td>
              </tr>
            ) : currentStudents.length === 0 ? (
              <tr>
                <td
                  colSpan={tableColSpan}
                  className="py-6 px-6 text-center text-gray-500"
                >
                  {searchTerm || hasActiveFilters() ? (
                    <div>
                      <p className="font-medium text-base">
                        No matching records found
                      </p>
                      <p className="text-sm mt-2">
                        Try adjusting your search criteria or filters
                      </p>
                    </div>
                  ) : (
                    <p className="font-medium text-base">
                      No students available
                    </p>
                  )}
                </td>
              </tr>
            ) : (
              currentStudents.map((student, index) => {
                const details =
                  completedDetailsMap[String(student.student_id)] ||
                  EMPTY_COMPLETED_DETAILS;
                return (
                <tr
                  key={student.student_id}
                  className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-indigo-50/40"
                  onClick={() => handleRowClick(student.student_id)}
                >
                  <td className="py-3 px-6 text-sm">
                    {indexOfFirstStudent + index + 1}
                  </td>
                  {showCompletedOnly ? (
                    <>
                      <td className="py-3 px-6 text-sm font-medium text-gray-800 whitespace-nowrap">
                        {student.username}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 whitespace-normal break-words">
                        {details.university}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 whitespace-nowrap">
                        {details.country}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 whitespace-nowrap">
                        {details.intake}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 whitespace-nowrap">
                        {details.course}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 whitespace-nowrap">
                        {student.phone_number || "—"}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 whitespace-nowrap">
                        {student.email}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-3 px-6 text-sm font-medium text-gray-800">
                        {student.username}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700">
                        {student.email}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 hidden sm:table-cell">
                        {student.phone_number || "—"}
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-700 hidden md:table-cell">
                        {student.city || "—"}
                      </td>
                      <td className="py-3 px-6">
                        <span
                          title={formatStatusLabel(student.status)}
                          className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ring-1 ${getStatusBadgeClass(
                            student.status
                          )} overflow-visible`}
                        >
                          {formatStatusLabel(student.status)}
                        </span>
                      </td>
                    </>
                  )}
                  <td className="py-3 px-6">
                    <div
                      className="flex flex-col items-center space-y-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) =>
                          handleToggleStatus(
                            student.student_id,
                            student.open_visa_page,
                            e
                          )
                        }
                        className={`rounded-full px-4 py-1 text-xs font-medium text-white shadow-sm ${
                          student.open_visa_page === true
                            ? "bg-rose-500 hover:bg-rose-600"
                            : "bg-emerald-500 hover:bg-emerald-600"
                        } transition-colors ${
                          loadingIds.includes(student.student_id)
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={loadingIds.includes(student.student_id)}
                      >
                        {loadingIds.includes(student.student_id) ? (
                          <span className="flex items-center space-x-1">
                            <span className="animate-spin h-3 w-3 border-2 border-white border-opacity-50 border-t-white rounded-full"></span>
                            <span>...</span>
                          </span>
                        ) : student.open_visa_page === true ? (
                          "Disable"
                        ) : (
                          "Enable"
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-6">
                    <div
                      className="flex flex-col items-center space-y-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => handleCrmAccessToggle(student, e)}
                        className={`rounded-full px-4 py-1 text-xs font-medium text-white shadow-sm ${
                          student.user?.is_active === true
                            ? "bg-rose-500 hover:bg-rose-600"
                            : "bg-emerald-500 hover:bg-emerald-600"
                        } transition-colors ${
                          loadingIds.includes(student.student_id)
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={loadingIds.includes(student.student_id)}
                      >
                        {loadingIds.includes(student.student_id) ? (
                          <span className="flex items-center space-x-1">
                            <span className="animate-spin h-3 w-3 border-2 border-white border-opacity-50 border-t-white rounded-full"></span>
                            <span>...</span>
                          </span>
                        ) : student.user?.is_active === true ? (
                          "Disable"
                        ) : (
                          "Enable"
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-6">
                    <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-2">
                      <button
                        onClick={(e) =>
                          handleArchiveToggle(
                            student.student_id,
                            !archived,
                            e
                          )
                        }
                        className={`rounded-full px-4 py-1 text-xs font-medium text-white shadow-sm transition-colors w-full whitespace-nowrap ${
                          archived
                            ? "bg-indigo-500 hover:bg-indigo-600"
                            : "bg-amber-500 hover:bg-amber-600"
                        }`}
                      >
                        {archived ? "Unarchive" : "Archive"}
                      </button>

                      {!archived && (
                        <button
                          onClick={(e) =>
                            handleStatusCompleteToggle(
                              student.student_id,
                              !showCompletedOnly,
                              e
                            )
                          }
                          className={`rounded-full px-4 py-1 text-xs font-medium text-white shadow-sm transition-colors w-full whitespace-nowrap ${
                            showCompletedOnly
                              ? "bg-rose-500 hover:bg-rose-600"
                              : "bg-emerald-500 hover:bg-emerald-600"
                          }`}
                        >
                          {showCompletedOnly ? "Mark as Incomplete" : "Mark as Complete"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls - enhanced with previous/next buttons */}
      {!loading && totalPages > 0 && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
          <div className="text-sm text-gray-600 mb-4 sm:mb-0">
            Showing {indexOfFirstStudent + 1} to{" "}
            {Math.min(indexOfLastStudent, filteredStudents.length)} of{" "}
            {filteredStudents.length} entries
          </div>

          <div className="flex items-center">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`mx-1 px-2 h-8 flex items-center justify-center border rounded ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                  : "bg-white border-gray-300 hover:bg-gray-100 text-gray-700"
              }`}
            >
              Previous
            </button>

            {/* First page always shown */}
            {totalPages > 5 && currentPage > 3 && (
              <>
                <button
                  onClick={() => setCurrentPage(1)}
                  className="mx-1 w-8 h-8 flex items-center justify-center border rounded bg-white border-gray-300 hover:bg-gray-100"
                >
                  1
                </button>
                {currentPage > 4 && <span className="mx-1">...</span>}
              </>
            )}

            {/* Page buttons */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                if (totalPages <= 5) return true;
                if (page === 1 || page === totalPages) return false;

                return page >= currentPage - 1 && page <= currentPage + 1;
              })
              .map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`mx-1 w-8 h-8 flex items-center justify-center border rounded ${
                    currentPage === page
                      ? "bg-green-500 text-white border-green-500"
                      : "bg-white border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {page}
                </button>
              ))}

            {/* Last page always shown */}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && (
                  <span className="mx-1">...</span>
                )}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="mx-1 w-8 h-8 flex items-center justify-center border rounded bg-white border-gray-300 hover:bg-gray-100"
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className={`mx-1 px-2 h-8 flex items-center justify-center border rounded ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                  : "bg-white border-gray-300 hover:bg-gray-100 text-gray-700"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationListTable;
